import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { ImageUpload } from "@/components/ImageUpload";
import { GeotagInput } from "@/components/GeotagInput";
import { MunicipalitySelect } from "@/components/MunicipalitySelect";
import { SearchableSelect } from "@/components/SearchableSelect";

export function FormField({ field, value, onChange, error }) {
  const tid = `field-${field.name}`;

  const render = () => {
    switch (field.type) {
      case "text":
        return (
          <Input
            className="h-12"
            value={value || ""}
            inputMode={field.inputMode}
            maxLength={field.maxLength}
            onChange={(e) => onChange(e.target.value)}
            data-testid={tid}
          />
        );
      case "number":
        return (
          <Input
            type="number"
            className="h-12"
            min={field.min}
            step={field.step}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={tid}
          />
        );
      case "textarea":
        return <Textarea rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
      case "image":
        return <ImageUpload value={value} onChange={onChange} testId={tid} />;
      case "geotag":
        return <GeotagInput value={value} onChange={onChange} testId={tid} />;
      case "radio":
        return (
          <RadioGroup value={value || ""} onValueChange={onChange} className="flex gap-6" data-testid={tid}>
            {field.options.map((opt) => (
              <div key={opt} className="flex items-center space-x-2">
                <RadioGroupItem value={opt} id={`${field.name}-${opt}`} data-testid={`${tid}-${opt.toLowerCase()}`} />
                <Label htmlFor={`${field.name}-${opt}`} className="cursor-pointer font-normal">{opt}</Label>
              </div>
            ))}
          </RadioGroup>
        );
      case "select":
        return <SearchableSelect value={value || ""} onChange={onChange} options={field.options} testId={tid} />;
      case "checkbox":
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={tid}>
            {field.options.map((opt) => {
              const arr = Array.isArray(value) ? value : [];
              const checked = arr.includes(opt);
              return (
                <label key={opt} className="flex items-center space-x-2 cursor-pointer">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(c) => onChange(c ? [...arr, opt] : arr.filter((o) => o !== opt))}
                    data-testid={`${tid}-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                  />
                  <span className="text-sm">{opt}</span>
                </label>
              );
            })}
          </div>
        );
      default:
        return null;
    }
  };

  if (field.type === "municipality") {
    return (
      <div className="space-y-2">
        <MunicipalitySelect value={value} onChange={onChange} testId={tid} />
        {error && <p className="text-xs text-red-500" data-testid={`${tid}-error`}>{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-slate-800">
        {field.label} {field.required && <span className="text-red-500">*</span>}
      </Label>
      {field.helpText && <p className="text-xs text-slate-500">{field.helpText}</p>}
      {render()}
      {error && <p className="text-xs text-red-500" data-testid={`${tid}-error`}>{error}</p>}
    </div>
  );
}
